@let state = flow.state();

<div
    class="booking-flow vstack gap-3"
    [class.booking-flow--in-progress]="flow.inProgress()"
    [class.booking-flow--loading]="state.step === 'loading'"
    appVenueColour
    [primaryColour]="venue.primaryColour"
>
    <ng-template #bookingProgress let-progress="progress">
        <div
            class="booking-flow-progress d-none d-md-flex"
            data-testid="storefront-booking-progress"
            role="progressbar"
            aria-label="Booking progress"
            aria-valuemin="1"
            [attr.aria-valuenow]="progress.currentStep"
            [attr.aria-valuemax]="progress.totalSteps"
            [attr.aria-valuetext]="'Step ' + progress.currentStep + ' of ' + progress.totalSteps"
        >
            @for (step of progress.steps; track step; let stepIndex = $index) {
                <span
                    class="booking-flow-progress-step"
                    [class.booking-flow-progress-step--complete]="stepIndex + 1 < progress.currentStep"
                    [class.booking-flow-progress-step--current]="stepIndex + 1 === progress.currentStep"
                    aria-hidden="true"
                ></span>
            }
        </div>
    </ng-template>

    <ng-template #diaryNote let-note>
        @if (note) {
            <div class="alert alert-info mb-md-0 m-1 m-md-0" role="alert">
                <div class="hstack gap-3 justify-content-center text-center">
                    <div class="hstack gap-2">
                        <i class="bi bi-info-circle-fill text-info"></i>
                    </div>
                    <div class="hstack gap-2">
                        <span class="fw-semibold" [innerHTML]="note"></span>
                    </div>
                </div>
            </div>
        }
    </ng-template>

    @if (state.step !== 'details' || flow.basketReview()) {
        @if (flow.progress(); as progress) {
            <ng-container
                [ngTemplateOutlet]="bookingProgress"
                [ngTemplateOutletContext]="{ progress }"
            ></ng-container>
        }
    }

    @if (flow.basketReview()) {
        <section class="booking-basket" aria-labelledby="booking-basket-title" data-testid="booking-basket-review">
            @if (flow.bookingSummary(); as summary) {
                <app-venue-storefront-booking-progress-summary
                    [search]="flow.basketItems()[0].searchCriteria"
                    [backDisabled]="!flow.canGoBack()"
                    (back)="flow.goBack()"
                />

                <article class="booking-basket-card">
                    <h2 id="booking-basket-title" class="h4 fw-semibold m-0">Your bookings</h2>
                    <ol class="booking-basket-list list-unstyled m-0" aria-label="Selected bookings">
                        @for (item of summary.items; track item.id; let index = $index; let last = $last) {
                            <li class="booking-basket-row" data-testid="booking-basket-row">
                                @if (item.imageUrl; as imageUrl) {
                                    <img class="booking-basket-reason-image" [src]="imageUrl" width="92" height="92" alt="" />
                                } @else {
                                    <div class="booking-basket-reason-image booking-basket-image-placeholder" aria-hidden="true">
                                        <i class="bi bi-calendar-event"></i>
                                    </div>
                                }
                                <div class="booking-basket-item-details">
                                    <h3 class="h6 fw-semibold m-0">{{ item.reasonName }}</h3>
                                    <div class="booking-basket-time small text-secondary">
                                        <img src="assets/clock_small.svg" width="20" height="20" alt="" />
                                        <span>
                                            <time [attr.datetime]="item.start.toISOString()">{{ item.start | date:'shortTime' }}</time>
                                            &ndash;
                                            <time [attr.datetime]="item.end.toISOString()">{{ item.end | date:'shortTime' }}</time>
                                        </span>
                                    </div>
                                    @if (item.areaName; as areaName) {
                                        <span class="small text-secondary">{{ areaName }}</span>
                                    }
                                </div>
                                <div class="booking-basket-item-actions">
                                    @if (item.depositAmount > 0) {
                                        <div class="booking-basket-item-payment">
                                            <span class="small text-secondary">{{ item.paymentLabel ?? 'Deposit' }}</span>
                                            <strong>{{ null | currencyUnit:item.depositAmount:summary.currencyCode }}</strong>
                                        </div>
                                    }
                                    <button type="button" class="btn btn-outline-secondary btn-sm booking-basket-remove"
                                        [disabled]="flow.basketBusy()"
                                        [attr.aria-label]="'Remove ' + item.reasonName + (last ? '' : ' and later bookings')"
                                        (click)="flow.removeBasketFrom(index)">
                                        {{ last ? 'Remove' : 'Remove this and later bookings' }}
                                    </button>
                                </div>
                                @if (item.extras.length > 0) {
                                    <div class="booking-basket-order">
                                        <h4 class="small fw-semibold m-0">Your order</h4>
                                        <ul class="list-unstyled m-0 vstack gap-2" data-testid="booking-basket-order">
                                            @for (extra of item.extras; track $index) {
                                                <li class="booking-basket-order-line small">
                                                    <span>
                                                        {{ extra.quantity }} &times; {{ extra.name }}
                                                        @if (extra.unitAmount !== undefined) {
                                                            <span class="d-block text-secondary">{{ null | currencyUnit:extra.unitAmount:summary.currencyCode }} each</span>
                                                        }
                                                    </span>
                                                    @if (extra.unitAmount !== undefined) {
                                                        <span class="text-nowrap">{{ null | currencyUnit:extra.unitAmount * extra.quantity:summary.currencyCode }}</span>
                                                    }
                                                </li>
                                            }
                                        </ul>
                                    </div>
                                }
                            </li>
                        }
                    </ol>

                    <footer class="booking-basket-footer" data-testid="booking-basket-actions">
                        @if (summary.payment; as payment) {
                            <div class="booking-basket-total">
                                <span class="fw-semibold">Total due at checkout</span>
                                <strong class="h5 m-0">{{ null | currencyUnit:payment.amount:summary.currencyCode }}</strong>
                            </div>
                        }
                        <div class="booking-basket-actions">
                            <button type="button" class="btn btn-outline-primary" [disabled]="flow.basketBusy() || flow.basketItems().length >= 20"
                                (click)="flow.addBasketReason()">Add something else</button>
                            <button type="button" class="btn btn-primary" [disabled]="flow.basketBusy()" (click)="flow.continueBasket()">Finish my booking</button>
                        </div>
                    </footer>
                </article>
            }
        </section>
    } @else {
    @if (flow.basketItems().length > 0 && state.step !== 'confirmation' && state.step !== 'details' && state.step !== 'waiting') {
        <aside class="booking-basket-context p-3" aria-label="Already in your booking" data-testid="booking-basket-context">
            <h2 class="h6 fw-semibold">Already in your booking</h2>
            <ul class="list-unstyled vstack gap-2 mb-0">
                @for (item of flow.selectedBookings(); track item.id) {
                    <li class="d-flex flex-wrap justify-content-between gap-2">
                        <strong>{{ item.name }}</strong>
                        <span>{{ item.start | date:'shortTime' }} &ndash; {{ item.end | date:'shortTime' }}</span>
                    </li>
                }
            </ul>
        </aside>
    }
    @switch (state.step) {
        @case ('order-payment') {
            <section class="bg-white border rounded-3 p-4 vstack gap-3" aria-labelledby="order-payment-heading" data-testid="order-payment-step">
                @if (state.bookingDetails.statusType === 'Booked') {
                    <h2 class="h4 fw-semibold mb-0" data-testid="booking-confirmed-status">Booking Confirmed</h2>
                } @else if (state.bookingDetails.statusType === 'Requested') {
                    <h2 class="h4 fw-semibold mb-0">Booking Requested</h2>
                }
                <h2 id="order-payment-heading" class="h4 fw-semibold mb-0">Pay for your products</h2>
                <p class="mb-0">Your card has been saved for the booking guarantee. Pay for your products now, or return through your booking link to pay later.</p>
                @if (state.bookingDetails.sale; as sale) {
                    @for (line of sale.order.lineItems; track line.id) {
                        <div class="d-flex justify-content-between gap-3">
                            <span>{{ line.quantity }} × {{ line.name }}
                                @for (modifier of line.modifiers; track $index) {
                                    <small class="d-block text-secondary">{{ modifier.name }}</small>
                                }
                                @if (line.paidOnline) { <app-booking-product-refund-policy [policy]="line" /> }
                            </span>
                            @if (line.paidOnline) { <strong>{{ null | currencyUnit:line.unitAmount() * line.quantity:sale.order.currencyCode }}</strong> }
                        </div>
                    }
                    <div class="d-flex justify-content-between border-top pt-3 fw-semibold">
                        <span>Total to pay</span><span>{{ null | currencyUnit:sale.order.total():sale.order.currencyCode }}</span>
                    </div>
                }
                @if (state.isPreparingPayment) { <p role="status">Preparing secure payment details…</p> }
                @if (state.errorMessage) { <p role="alert" class="text-danger">{{ state.errorMessage }}</p> }
                @if (state.paymentIntent; as intent) {
                    <app-venue-storefront-booking-payment-element #orderPayment
                        [venue]="venue" [intent]="intent" usage="payment"
                        [billingEmailAddress]="state.bookingDetails.bookingEmailAddress ?? ''" [currencyCode]="venue.currencyCode"
                        (readyChange)="flow.setOrderPaymentReady($event)"
                        (confirmingChange)="flow.setPaymentConfirming($event)"
                        (confirmed)="flow.paymentConfirmed($event)" />
                    <button class="btn btn-primary" type="button" [disabled]="!state.paymentReady || state.isConfirmingPayment"
                        (click)="orderPayment.confirm()">Pay for products</button>
                }
                @if (state.bookingDetails.statusType === 'Booked' || state.bookingDetails.statusType === 'Requested') {
                    <button class="btn btn-outline-secondary" type="button" [disabled]="state.isConfirmingPayment"
                        data-testid="pay-products-later" (click)="flow.payForOrderLater()">Pay later</button>
                }
            </section>
        }
        @case ('loading') {
            <section
                class="booking-flow-stage booking-flow-loading d-flex flex-column align-items-center justify-content-center gap-3 text-center"
                data-testid="storefront-booking-loading"
                role="status"
                aria-live="polite"
                aria-atomic="true"
            >
                <div
                    class="booking-flow-loading-indicator d-flex flex-column gap-3 align-items-center bg-white rounded-3 shadow-sm p-4 mx-3"
                >
                    <span
                        class="spinner-border text-primary"
                        aria-hidden="true"
                    ></span>
                    <div class="vstack gap-1">
                        <h2 class="h5 fw-semibold mb-0">Searching for availability</h2>
                        <p class="text-secondary mb-0">
                            We're checking the best options for your booking.
                        </p>
                    </div>
                </div>
            </section>
        }
        @case ('reason') {
            <div class="booking-flow-stage booking-flow-reason vstack gap-3">
                <app-venue-storefront-booking-progress-summary
                    [search]="state.searchCriteria"
                    [backDisabled]="!flow.canGoBack()"
                    (back)="flow.goBack()"
                />

                <ng-container
                    [ngTemplateOutlet]="diaryNote"
                    [ngTemplateOutletContext]="{ $implicit: state.availability.publicDiaryNoteOnDate(state.searchCriteria.date) }"
                ></ng-container>

                <app-venue-storefront-booking-reason-step
                    [groups]="state.bookingChoiceGroups"
                    [gridLayout]="venue.shouldUseVenueStorefrontChoiceGrid()"
                    [search]="state.searchCriteria"
                    [paymentTerminology]="paymentTerminology"
                    (choiceSelected)="showChoiceDetails($event, state.searchCriteria)"
                />
            </div>
        }
        @case ('slots') {
            <div class="booking-flow-stage booking-flow-slots vstack gap-3">
                <app-venue-storefront-booking-progress-summary
                    [search]="state.searchCriteria"
                    [backDisabled]="!flow.canGoBack()"
                    (back)="flow.goBack()"
                />

                <ng-container
                    [ngTemplateOutlet]="diaryNote"
                    [ngTemplateOutletContext]="{ $implicit: state.availability.publicDiaryNoteOnDate(state.searchCriteria.date) }"
                ></ng-container>

                <app-venue-storefront-booking-slot-step
                    [groups]="state.areaGroups"
                    [idealTime]="state.searchCriteria.idealTime"
                    [selectedSlot]="state.selectedSlot"
                    (slotSelected)="flow.selectBookingSlot($event)"
                    (waitlistSlotSelected)="flow.selectWaitlistSlot($event)"
                    (joinWaitlistRequested)="flow.joinWaitlist()"
                />
            </div>
        }
        @case ('waitlist') {
            <div class="booking-flow-stage booking-flow-waitlist vstack gap-3">
                <app-venue-storefront-booking-progress-summary
                    [search]="state.searchCriteria"
                    [backDisabled]="!flow.canGoBack()"
                    (back)="flow.goBack()"
                />

                <ng-container
                    [ngTemplateOutlet]="diaryNote"
                    [ngTemplateOutletContext]="{ $implicit: state.availability.publicDiaryNoteOnDate(state.searchCriteria.date) }"
                ></ng-container>

                <app-waitlist-form
                    (submissionStatusChange)="tracking.recordFormProgress('waitlist', $event)"
                    [availability]="state.availability"
                    [requiresDogFriendly]="state.searchCriteria.requiresDogFriendly"
                    [requiresWheelchairAccess]="state.searchCriteria.requiresWheelchairAccess"
                    [requiresPramAccessible]="state.searchCriteria.requiresPramAccessible"
                    [requiresHighchairFriendly]="state.searchCriteria.requiresHighchairFriendly"
                    [partySize]="state.searchCriteria.partySize"
                    [eventId]="state.eventId"
                    [reasonId]="state.reasonId"
                    [date]="state.searchCriteria.date"
                    [idealTime]="state.searchCriteria.idealTime"
                    [organisationId]="venue.organisationId"
                    [businessId]="venue.businessId"
                    [venueId]="venue.id"
                />
            </div>
        }
        @case ('upsell') {
            <div class="booking-flow-stage booking-flow-upsell vstack gap-3">
                <app-venue-storefront-booking-progress-summary
                    [search]="state.searchCriteria"
                    [backDisabled]="!flow.canGoBack()"
                    (back)="flow.goBack()"
                />

                <ng-container
                    [ngTemplateOutlet]="diaryNote"
                    [ngTemplateOutletContext]="{ $implicit: state.availability.publicDiaryNoteOnDate(state.searchCriteria.date) }"
                ></ng-container>

                <app-venue-storefront-booking-upsell-step
                    [availability]="state.availability"
                    [reasonId]="state.reasonId"
                    [partySize]="state.searchCriteria.partySize"
                    [lineItems]="state.lineItems"
                    [showPrices]="!!state.availability.bookingUpsellMenu(state.reasonId)?.isPaid"
                    (lineItemsChange)="flow.updateUpsellLineItems($event)"
                    (confirmed)="flow.confirmUpsells($event)"
                />
            </div>
        }
        @case ('policy') {
            <div class="booking-flow-stage booking-flow-policy vstack gap-3">
                <app-venue-storefront-booking-progress-summary
                    [search]="state.searchCriteria"
                    [backDisabled]="!flow.canGoBack()"
                    (back)="flow.goBack()"
                />

                <ng-container
                    [ngTemplateOutlet]="diaryNote"
                    [ngTemplateOutletContext]="{ $implicit: state.availability.publicDiaryNoteOnDate(state.searchCriteria.date) }"
                ></ng-container>

                <app-venue-storefront-booking-policy-step
                    [policies]="state.policies"
                    (accepted)="flow.acceptBookingPolicy()"
                />
            </div>
        }
        @case ('details') {
            <div class="booking-flow-stage booking-flow-details">
                @if (state.selectedSlot.reservableOption; as option) {
                    <div class="row g-4 align-items-start booking-flow-details-layout">
                        <div class="col-12 col-lg-8">
                            <div class="booking-flow-details-main vstack gap-3">
                                @if (flow.progress(); as progress) {
                                    <ng-container
                                        [ngTemplateOutlet]="bookingProgress"
                                        [ngTemplateOutletContext]="{ progress }"
                                    ></ng-container>
                                }

                                <app-venue-storefront-booking-progress-summary
                                    [search]="state.searchCriteria"
                                    [backDisabled]="!flow.canGoBack()"
                                    (back)="flow.goBack()"
                                />

                                <ng-container
                                    [ngTemplateOutlet]="diaryNote"
                                    [ngTemplateOutletContext]="{ $implicit: state.availability.publicDiaryNoteOnDate(state.searchCriteria.date) }"
                                ></ng-container>

                                <app-venue-storefront-booking-details-step
                                    [venue]="venue"
                                    [initialDetails]="state.details"
                                    [basketReasonIds]="flow.basketReasonIds()"
                                    [reasonId]="state.reasonId"
                                    [eventId]="state.eventId"
                                    [partySize]="state.searchCriteria.partySize"
                                    [bookingEndTime]="flow.bookingSummary()?.endDateTime ?? state.selectedSlot.bookingSlot.endDateTime(option.durationMinutes)"
                                    [upsellItems]="flow.bookingSummary()?.extras ?? []"
                                    [paymentRequirement]="state.paymentRequirement"
                                    [paymentIntent]="state.paymentIntent"
                                    [paymentUsage]="state.paymentUsage"
                                    [isPreparingPayment]="state.isPreparingPayment"
                                    [showPaymentSection]="state.showPaymentSection"
                                    [paymentRequirementResolved]="state.paymentRequirementResolved"
                                    [isSubmitting]="state.isSubmitting"
                                    [errorMessage]="state.errorMessage"
                                    [bookingSlotUnavailable]="state.bookingSlotUnavailable"
                                    [emailValidationFailed]="state.emailValidationFailed"
                                    (paymentPreparationRequested)="flow.preparePaymentWithDetails($event)"
                                    (detailsChanged)="flow.detailsChanged($event)"
                                    (submissionStatusChange)="tracking.recordFormProgress('details', $event)"
                                    (paymentStatusChange)="tracking.recordPaymentProgress($event)"
                                    (detailsSubmitted)="flow.continueWithDetails($event)"
                                    (paymentFinalisingChange)="flow.setPaymentConfirming($event)"
                                    (paymentConfirmed)="flow.paymentConfirmed($event)"
                                />
                            </div>
                        </div>

                        @if (flow.bookingSummary(); as bookingSummary) {
                            <div class="booking-flow-details-summary d-lg-block col-lg-4" [class.d-none]="flow.basketItems().length === 0">
                                <app-venue-storefront-booking-summary-card
                                    [summary]="bookingSummary"
                                />
                            </div>
                        }
                    </div>
                }
            </div>
        }
        @case ('waiting') {
            <section
                class="booking-flow-stage booking-flow-waiting d-flex flex-column align-items-center justify-content-center gap-3 text-center"
                data-testid="storefront-booking-waiting"
                [attr.role]="state.errorMessage ? 'alert' : 'status'"
                aria-live="polite"
                aria-atomic="true"
            >
                @if (state.errorMessage; as errorMessage) {
                    <div class="alert alert-light border mb-0">
                        <h2 class="h5 fw-semibold">We're still confirming your booking</h2>
                        <p class="mb-0">{{ errorMessage }}</p>
                    </div>
                } @else {
                    <span class="spinner-border text-primary" aria-hidden="true"></span>
                    <div class="vstack gap-1">
                        <h2 class="h5 fw-semibold mb-0">Confirming your booking</h2>
                        <p class="text-secondary mb-0">
                            Please keep this page open while we finish up.
                        </p>
                    </div>
                }
            </section>
        }
        @case ('confirmation') {
            <div class="booking-flow-stage booking-flow-confirmation">
                <app-confirmation [details]="state.bookingDetails" (bookAgainSelected)="flow.returnToSearch()" />
            </div>
        }
        @default {
            <div class="booking-flow-stage booking-flow-search vstack">
                @if (state.inSession && state.searchMode === 'availability') {
                    <button
                        type="button"
                        class="btn btn-outline-secondary m-1 mb-md-3 d-inline-flex align-items-center gap-1 w-auto align-self-start"
                        data-testid="back-booking-step"
                        [attr.aria-label]="flow.basketItems().length > 0 ? 'Back to your bookings' : 'Back to venue page'"
                        [disabled]="!flow.canReturnToVenue()"
                        (click)="flow.returnToVenue()"
                    >
                        <img
                            src="assets/left/back.svg"
                            width="20"
                            height="20"
                            alt=""
                            aria-hidden="true"
                        />
                        <span>Back</span>
                    </button>
                }

                <app-venue-storefront-booking-search
                    [venue]="venue"
                    [initialValues]="state.searchCriteria"
                    [largePartyEnquiry]="state.searchMode === 'large-party-enquiry'"
                    (enquiryStatusChange)="tracking.recordFormProgress('enquiry', $event)"
                    (availabilityStatusChange)="tracking.recordAvailabilityStatus($event)"
                    (bookingContextResolved)="flow.showSearchPreview()"
                    (searchCriteriaChanged)="tracking.recordSearchInteraction($event)"
                    (searchAvailability)="flow.search($event)"
                    (largePartyEnquiryRequested)="flow.enterLargePartyEnquiry($event)"
                    (storefrontReturnRequested)="flow.returnToVenue()"
                />

                @if (state.errorMessage; as errorMessage) {
                    <div
                        class="alert alert-light border mb-0"
                        data-testid="storefront-search-error"
                        role="alert"
                    >
                        {{ errorMessage }}
                    </div>
                }
            </div>
        }
    }
    }
</div>
